import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { WidthObserver } from '@atlaskit/width-detector';
import {
	CornerStyle,
	Height,
	ItemSelectedToEditWrapper,
	useExtensionsState,
	useProductDependencies,
} from '@atlassian/company-hub-common';
import { VERTICAL_MAX_WIDTH } from '@atlassian/company-hub-common/spotlight-layout';

import { carouselContentAnalyticSource } from '../../constants';
import { type CarouselParameters, NavigationStyle } from '../../types';

import { FrameStrip } from './frame-strip';
import { CarouselLayoutRenderer } from './layout-renderer';
import { NavigationThumbnail } from './navigation-thumbnail';

const IMAGE_PERCENTAGE = 0.575;
export type CarouselContentV2Props = {
	contentId: string;
	extensionLocalId: string;
	isCompanyHub?: boolean;
	isInViewMode?: boolean;
	parameters: CarouselParameters;
};

const styles = cssMap({
	containerStyles: {
		display: 'flex',
		position: 'relative',
		flexDirection: 'row',
		overflow: 'hidden',
		width: '100%',
		justifyContent: 'flex-start',
		scrollSnapType: 'x mandatory',
	},
	previewStyles: { pointerEvents: 'none' },
});

export const isCarouselVisible = (carouselRef: React.RefObject<HTMLDivElement>) => {
	if (!carouselRef.current) {
		return false;
	}
	const rect = carouselRef.current.getBoundingClientRect();
	return rect.top >= 0 && rect.bottom <= window.innerHeight;
};

export const CarouselContentV2 = ({
	extensionLocalId,
	parameters,
	contentId,
	isCompanyHub,
	isInViewMode,
}: CarouselContentV2Props): React.JSX.Element => {
	const [{ selectedToEditCardId }] = useExtensionsState();
	const { smartCardProviderComponent } = useProductDependencies();
	const SmartCardProvider = smartCardProviderComponent || Fragment;
	const [selectedFrameIndex, setSelectedFrameIndex] = useState<number>(0);
	// Separately track the index of the hero card that should be scrolled to.
	// This should be in sync with selected frame index above, but this one
	// starts at -1 to prevent scrolling on mount.
	const [scrollToHeroFrameAtIndex, setScrollToHeroFrameAtIndex] = useState<number>(-1);
	const { cards: frames, cornerStyle, isAutoplayEnabled, navigationStyle } = parameters;
	const hasRoundedCorners = cornerStyle === CornerStyle.ROUNDED || !isCompanyHub || !cornerStyle;
	const [isHovered, setIsHovered] = useState<boolean>(false);

	const [width, setWidth] = useState(VERTICAL_MAX_WIDTH + 1);

	const selectFrameAtIndex = useCallback(
		(requestedIndex: number) => {
			setScrollToHeroFrameAtIndex(requestedIndex);
			setSelectedFrameIndex(requestedIndex);
		},
		[setScrollToHeroFrameAtIndex, setSelectedFrameIndex],
	);

	useEffect(() => {
		if (selectedFrameIndex > frames.length - 1) {
			selectFrameAtIndex(frames.length - 1);
		}
		if (!isInViewMode && selectedToEditCardId) {
			const index = frames.findIndex((card) => card.cardId === selectedToEditCardId);
			if (index !== -1) {
				selectFrameAtIndex(index);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [frames.length, selectedFrameIndex, selectFrameAtIndex, selectedToEditCardId, isInViewMode]);

	const carouselRef = useRef<HTMLDivElement>(null);

	const spotlightHero = (
		<Box xcss={cx(styles.containerStyles)}>
			{frames.map(
				(
					{
						cardId,
						backgroundColor,
						layout,
						imageSrc,
						imageAltText,
						title,
						description,
						textColor,
						link,
						buttonStyle,
						buttonText,
					},
					index,
				) => {
					return (
						<CarouselLayoutRenderer
							key={cardId}
							index={index}
							frameSetLength={frames.length}
							selectedFrameIndex={scrollToHeroFrameAtIndex}
							extensionLocalId={extensionLocalId}
							testId={`carousel-spotlight-${index}--${index === selectedFrameIndex ? 'active' : 'not-active'}`}
							backgroundColor={backgroundColor}
							height={Height.MEDIUM}
							layout={layout}
							imageData={imageSrc}
							imageAltText={imageAltText}
							width={width}
							imageWidthPercentage={IMAGE_PERCENTAGE}
							title={title}
							description={description}
							textColor={textColor}
							link={link}
							buttonStyle={buttonStyle}
							buttonText={buttonText}
							contentId={contentId}
							hasRoundedCorners={hasRoundedCorners}
							source={carouselContentAnalyticSource}
						/>
					);
				},
			)}
			<WidthObserver setWidth={setWidth} offscreen />
		</Box>
	);

	const handleMouseOrFocusChange = (event: 'enter' | 'leave') => {
		if (event === 'enter') {
			setIsHovered(true);
		} else {
			setIsHovered(false);
		}
	};

	const content = (
		<Box
			onMouseEnter={() => handleMouseOrFocusChange('enter')}
			onMouseLeave={() => handleMouseOrFocusChange('leave')}
			onFocusCapture={() => handleMouseOrFocusChange('enter')}
			onBlurCapture={() => handleMouseOrFocusChange('leave')}
			ref={carouselRef}
		>
			<Flex wrap="wrap" gap="space.150" testId="carousel-v2-content-container">
				{spotlightHero}
				<FrameStrip
					isInViewMode={!!isInViewMode}
					frameIds={frames.map((frame) => frame.cardId)}
					selectedHeroFrameIndex={selectedFrameIndex}
					setSelectedHeroFrameIndex={selectFrameAtIndex}
					extensionLocalId={extensionLocalId}
					isHovered={isHovered}
					isCarouselVisible={() => isCarouselVisible(carouselRef)}
					isAutoplayEnabled={isAutoplayEnabled && isInViewMode}
					navigationStyle={navigationStyle || NavigationStyle.THUMBNAILS}
					width={width}
					frames={frames.map((frame, index) => (
						<ItemSelectedToEditWrapper
							key={frame.cardId}
							itemId={frame.cardId}
							extensionLocalId={extensionLocalId}
						>
							<NavigationThumbnail
								contentId={contentId}
								wrapperHeight={40}
								minWidth={200}
								maxWidth={200}
								onSucceeded={() => {}}
								onFailed={() => {}}
								isEmpty={!frame.link}
								isSelected={selectedFrameIndex === index}
								hasRoundedCorners={hasRoundedCorners}
								{...frame}
							/>
						</ItemSelectedToEditWrapper>
					))}
				/>
			</Flex>
		</Box>
	);

	return <SmartCardProvider>{content}</SmartCardProvider>;
};
